import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function ErhvervsrengoeringPage() {
  return (
    <>
      <SEO
        title="Erhvervsrengøring"
        description="Pålidelig erhvervsrengøring i Havdrup og omegn. Et rent arbejdsmiljø for medarbejdere, kunder og besøgende med fleksible aftaler."
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#0d3836]/10 to-white py-12 sm:py-16 lg:py-20 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 flex items-center gap-2 text-xs text-slate-500" aria-label="Brødkrummer">
            <Link to="/" className="hover:text-slate-800 transition-colors">Forside</Link>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span>Ydelser</span>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span className="font-semibold text-slate-900">Erhvervsrengøring</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0d3836]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#0d3836]">
                <i className="fa-regular fa-building text-[#0d3836]" aria-hidden="true" />
                Erhvervsrengøring
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
                Pålidelig erhvervsrengøring til virksomheder
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                Et rent arbejdsmiljø gør en forskel for både medarbejdere, kunder og besøgende. Vi tilbyder fleksibel erhvervsrengøring, der tilpasses virksomhedens lokaler og behov.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#0d3836] px-6 py-3.5 text-sm font-semibold text-white shadow-xs hover:bg-[#154e4a] transition-colors text-center"
                >
                  <span>Få et tilbud til din virksomhed</span>
                  <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="tel:31341632"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors text-center"
                >
                  <i className="fa-regular fa-phone text-[#0d3836]" aria-hidden="true" />
                  <span className="no-break">31 34 16 32</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl bg-slate-100 shadow-md border border-slate-200/80 aspect-4/3">
                <img
                  src={imageUrl('/_assets/shutterstock-2200915291.jpg', 'width=800,quality=85,format=auto')}
                  alt="Professionel kontorrengøring og aftørring af arbejdspladser"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detaljer */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-display mb-4">
                  Et præsentabelt arbejdsmiljø uden afbrydelser
                </h2>
                <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
                  <p>
                    Uanset om I driver et kontor, et værksted, et agentur eller en butik, skaber et rent miljø trivsel for de ansatte og giver et professionelt indtryk over for kunder og samarbejdspartnere.
                  </p>
                  <p>
                    Vi tilpasser rengøringen efter jeres arbejdsgang og kan udføre opgaven tidligt om morgenen, om eftermiddagen eller uden for normal arbejdstid, så den daglige drift ikke forstyrres.
                  </p>
                </div>
              </div>

              {/* Hvad dækker vi typisk */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-4 flex items-center gap-2">
                  <i className="fa-regular fa-list-check text-[#0d3836]" aria-hidden="true" />
                  Typiske elementer i erhvervsrengøring
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Aftørring af arbejdsborde, IT-overflader og kontorinventar</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Mødelokaler med aftørring af borde, skærme og døre</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Køkken, kaffestationer, vask og overflader</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Sanitære faciliteter og toiletter med grundig afkalkning</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Støvsugning af gulvtæpper og vask af hårde gulve</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Tømning af affaldsspande og affaldssortering</span>
                  </div>
                </div>
              </div>

              {/* Hvad koster det sektion */}
              <div className="rounded-2xl border border-[#0d3836]/15 bg-[#0d3836]/5 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-2">
                  Hvad koster erhvervsrengøring?
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Prisen afhænger blandt andet af lokalerne, kvadratmeter, hvor ofte der skal gøres rent og de konkrete ønsker til opgaven. Vi tager udgangspunkt i den enkelte opgave, så I får en stabil og konkurrencedygtig aftale.
                </p>
                <div className="mt-4">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3836] hover:text-[#154e4a] transition-colors"
                  >
                    <span>Få et tilbud tilpasset jeres virksomhed</span>
                    <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Hurtig kontakt sidebar */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xs space-y-6">
                <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display">
                  Kontakt vedr. erhverv
                </h3>
                <p className="text-sm text-slate-600">
                  Vi kommer gerne forbi og besigtiger lokalerne for at give et præcist og uforpligtende tilbud.
                </p>

                <div className="space-y-4 pt-2">
                  <a
                    href="tel:31341632"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 hover:border-[#0d3836]/30 hover:bg-[#0d3836]/5 transition-colors group cursor-pointer"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] group-hover:bg-[#0d3836] group-hover:text-white transition-colors">
                      <i className="fa-regular fa-phone text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Ring direkte</p>
                      <p className="text-base font-bold text-slate-900 no-break">31 34 16 32</p>
                    </div>
                  </a>

                  <a
                    href="mailto:havdruprengoring@gmail.com"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 hover:border-[#0d3836]/30 hover:bg-[#0d3836]/5 transition-colors group cursor-pointer"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] group-hover:bg-[#0d3836] group-hover:text-white transition-colors">
                      <i className="fa-regular fa-envelope text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Skriv til os</p>
                      <p className="text-sm font-semibold text-slate-900 no-break">havdruprengoring@gmail.com</p>
                    </div>
                  </a>
                </div>

                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100 text-xs text-slate-600 space-y-2">
                  <div className="flex items-center gap-2">
                    <i className="fa-regular fa-clock text-[#0d3836]" aria-hidden="true" />
                    <span>Fleksible rengøringstider efter aftale</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-regular fa-user-check text-[#0d3836]" aria-hidden="true" />
                    <span>Fast kontaktperson & stabilitet</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
